import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

// UI Components
import Stack from "components/common/Stack";
import Loader from "components/common/Loader";
import Modal from "components/common/Modal";
import Image from "components/common/Image";
import IconButton from "components/common/IconButton";
import TextField from "components/common/TextField";
import Avatar from "components/common/Avatar";
import Button from "components/common/Button";
import Text from "components/common/Text";
import SectionTitle from "components/SectionTitle";
import JarvisButton from "components/JarvisButton";

// Icons
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Helpers / APIs
import _get from "lodash.get";
import { intercom } from "library/tracker/intercom";
import { config } from "config";
import { localStore } from "stores/localStorage";
import { validationHelper } from "helper/validation";
import { masterApis } from "stores/master/masterApis";
import { onboardingApis } from "stores/onboarding/onboardingApis";
import { onboardingParsers } from "stores/onboarding/onboardingParsers";

// Redux actions
import { customerActions } from "stores/customer/customerActions";
import { commonActions } from "stores/common/commonActions";
import RMSOnboardingHeader from "components/RMSOnboarding/RMSOnboardingHeader";
import { PHCOnboardingActions } from "stores/PHC/PHCOnboarding/PHCOnboardingAction";
import { isPhcPreviewWithAuthToken } from "helper/phcPreviewAuthToken";

const onboardingStages = [
  {
    stageId: ["UPLOAD_PORTFOLIO"],
    stageNumber: 1,
    heading: "Upload Portfolio",
    uri: "/phc/onboarding/portfolio-upload",
    subheading: "Upload or sync your existing portfolio to begin analysis.",
  },
  {
    stageId: ["PREVIEW"],
    stageNumber: 2,
    heading: "Preview Portfolio",
    uri: "/phc/onboarding/preview",
    subheading: "Review the imported stocks before generating insights.",
  },
  {
    stageId: ["GENERATE_PORTFOLIO"],
    stageNumber: 3,
    heading: "Generate Report",
    uri: "/phc/onboarding/generate-portfolio",
    subheading: "Complete your verification to proceed with analysis.",
  },
  {
    stageId: ["CKYC", "DOCUMENT_SIGNING"],
    stageNumber: 4,
    heading: "CKYC & E-Sign",
    uri: "/phc/onboarding/ckyc",
    subheading: "Complete your verification to proceed with analysis.",
  },
  {
    stageId: ["SAVE_REPORT"],
    stageNumber: 5,
    heading: "Save Report",
    uri: "/phc/onboarding/save-portfolio",
    subheading:
      "AI processes your portfolio to identify risks and missed gains.",
  },
];

function PHCOnboardingLayout({ children }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // -------------------------------------------------------------
  // REDUX STATE USING useSelector()
  // -------------------------------------------------------------
  const { currentStage, PHCOnboardingSteps, isPaymentCompleted } = useSelector(
    (state) => state.PHC.PHCOnboarding,
  );

  const {
    isEmailVerified,
    email: emailId,
    name,
  } = useSelector((state) => state.customer.loginProfile);

  const {
    isInitialFetched: isOnboardingDataFetched,
    loading,
    isOnboardingCompleted: isPortfolioOnboardingCompleted,
  } = useSelector((state) => state.onboarding);

  const isOnboardingDataLoading = loading?.onboardingData;

  // -------------------------------------------------------------
  // LOCAL STATE
  // -------------------------------------------------------------
  const [isHeaderVisible, setHeaderVisible] = useState(true);
  const [isEditEmailModal, setEditEmailModal] = useState(false);
  const [email, setEmail] = useState(emailId);
  const [emailError, setEmailError] = useState("");
  const [actionType, setActionType] = useState("");

  // -------------------------------------------------------------
  // ACTIVE STAGE HANDLERS
  // -------------------------------------------------------------
  const getActiveStage = useCallback(() => {
    return onboardingStages.find((o) => o.stageId.includes(currentStage));
  }, [currentStage]);

  const getActiveStage2 = useCallback(() => {
    const uri = location.pathname;
    return onboardingStages.find((o) => o.uri === uri) || onboardingStages[0];
  }, [location.pathname]);

  const activeStage2 = getActiveStage2();

  const isGraphDashboardStage =
    location.pathname.includes("/generate-portfolio") ||
    location.pathname.includes("/save-portfolio");

  // -------------------------------------------------------------
  // VALIDATION + EMAIL HANDLERS
  // -------------------------------------------------------------
  const validateEmail = () => {
    const val = validationHelper.email(email);
    setEmailError(val.message);
    return val.isValid;
  };

  const handleSendLink = async () => {
    if (!validateEmail()) return;

    try {
      const res = await onboardingApis.sendEmailVerificationLink({
        email,
        actionType,
      });
      const parsed = onboardingParsers.sendEmailVerificationLink(res);

      if (actionType === "EDIT") {
        dispatch(customerActions.updateEmail({ email }));
      }

      dispatch(commonActions.setToast({ message: parsed.message }));
      setEditEmailModal(false);
      setEmailError("");
    } catch (err) {
      dispatch(commonActions.setToast({ message: err?.data?.message }));
    }
  };

  const handleEditEmailModal = (isOpen, type) => {
    if (type === "CANCEL") {
      setEmail(emailId);
      setEmailError("");
      return setEditEmailModal(false);
    }
    if (type === "RESEND") {
      setActionType("RESEND");
      return handleSendLink();
    }

    setActionType(type);
    setEditEmailModal(isOpen);
  };

  // -------------------------------------------------------------
  // LOGOUT HANDLER
  // -------------------------------------------------------------
  const handleLogoutClick = async () => {
    await masterApis.updateLastActiveProduct();
    dispatch(customerActions.logout());
    localStore.removeSSOCustomer();
    localStore.removeSsoClientId();
    setTimeout(() => history.replace("/login"), 800);
  };

  // -------------------------------------------------------------
  // INITIAL LOAD + REDIRECTION
  // -------------------------------------------------------------

  const isPhcOnboardingCompleted = useSelector(
    (state) => state.PHC.PHCOnboarding.isOnboardingCompleted,
  );

  useEffect(() => {
    const previewAuthLink = isPhcPreviewWithAuthToken(
      location.pathname,
      location.search,
    );

    if (
      (!isOnboardingDataFetched ||
        !isOnboardingDataLoading ||
        _get(location, "state.paymentStatus") === "SUCCEED") &&
      !previewAuthLink
    ) {
      dispatch(PHCOnboardingActions.getOnboardingData());
    }

    if (isPhcOnboardingCompleted) {
      return history.replace("/customer/dashboard"); // old code to redirect if any onboarding completed  /phc/dashboard
    }

    const stage = getActiveStage();

    // With ?token= on preview, skip redirect only while API stage is still PREVIEW
    // (avoids bouncing away before data loads). Once stage advances after Continue,
    // allow redirect to generate-portfolio / ckyc / etc.
    const holdPreviewWithToken =
      previewAuthLink && currentStage === "PREVIEW";

    if (
      location.pathname !== "/phc/onboarding/overview" &&
      location.pathname !== stage?.uri &&
      !holdPreviewWithToken
    ) {
      history.replace({ pathname: stage?.uri, state: location.state });
    }
  }, [getActiveStage(), currentStage, location.pathname, location.search]);

  // -------------------------------------------------------------
  // SHOW / HIDE HEADER BASED ON ROUTE
  // -------------------------------------------------------------
  useEffect(() => {
    if (location.pathname === "/phc/onboarding/overview") {
      setHeaderVisible(false);
    } else {
      setHeaderVisible(true);
    }
  }, [location.pathname]);

  // -------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------

  return (
    <>
      {/* EMAIL VERIFY BANNER */}
      {!isEmailVerified && (
        <Stack
          sx={{
            backgroundColor: "#2B70C5",
            pl: 1,
            position: "sticky",
            top: 0,
            zIndex: 9,
          }}
          direction="row"
          alignItems="center"
          justifyContent="center"
          p={0.4}
        >
          <Text
            color="#fff"
            sx={{
              flex: 1,
              fontSize: { xs: "caption1.fontSize", md: "small.fontSize" },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: { xs: "left", md: "center" },
            }}
          >
            Verify your Email to proceed — link sent to {emailId}
          </Text>

          {/* Edit Email Icon */}
          <Avatar
            onClick={() => handleEditEmailModal(true, "EDIT")}
            sx={{ backgroundColor: "transparent", ml: 1, cursor: "pointer" }}
          >
            <Image
              src="/svgs/edit-file.svg"
              height={16}
              width={16}
              imgStyle={{ cursor: "pointer" }}
            />
          </Avatar>

          {/* Resend Button */}
          <Button
            size="small"
            onClick={() => handleEditEmailModal(true, "RESEND")}
            sx={{
              color: "#fff",
              textTransform: "capitalize",
              fontSize: "0.875rem",
              ml: 1,
            }}
            variant="text"
          >
            Resend
          </Button>
        </Stack>
      )}

      <Stack
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: isGraphDashboardStage ? "100%" : 1280,
          m: isGraphDashboardStage ? 0 : "0 auto 16px",
        }}
      >
        {/* HEADER */}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            p: 2,
            borderBottom: "1px solid #DEDEDE",
            px: isGraphDashboardStage ? { xs: 2, md: 3 } : 2,
          }}
        >
          <SectionTitle
            subheading={
              isPortfolioOnboardingCompleted
                ? "Back to Dashboard"
                : "Back to Product Page"
            }
            startIcon={
              <IconButton
                onClick={() => {
                  if (isPortfolioOnboardingCompleted) {
                    history.replace("/customer/dashboard"); // old code to redirect if any onboarding completed  /phc/dashboard
                  } else {
                    history.replace("/customer/dashboard"); // old code to redirect if any onboarding completed  /customer/product-info
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            }
          />

          <IconButton onClick={handleLogoutClick}>
            <LogoutIcon />
          </IconButton>
        </Stack>

        {/* BODY */}
        <Stack
          sx={{
            flex: 1,
            backgroundColor: "white",
            borderRadius: isGraphDashboardStage ? 0 : 1,
          }}
        >
          <RMSOnboardingHeader
            isOnbordingHeaderVisible={true}
            currentStage={currentStage}
            heading={activeStage2.heading}
            location={location}
            onboardingStages={onboardingStages}
            OnboardingSteps={PHCOnboardingSteps}
          />

          <Stack
            sx={{
              px: isGraphDashboardStage ? 0 : 3,
              py: isGraphDashboardStage ? 0 : 3,
              position: "relative",
            }}
          >
            <Loader loading={isOnboardingDataLoading} />
            {!isOnboardingDataLoading && children}
          </Stack>
        </Stack>
      </Stack>

      {/* EMAIL EDIT MODAL */}
      <Modal open={isEditEmailModal} size="xs" disableClose>
        <Text fontWeight={600} mb={2}>
          Edit Email Address
        </Text>

        <TextField
          label="Email"
          value={email}
          error={emailError}
          helperText={emailError}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
        />

        <Stack direction="row" justifyContent="flex-end">
          <JarvisButton
            variant="outlined"
            onClick={() => handleEditEmailModal(false, "CANCEL")}
          >
            Cancel
          </JarvisButton>

          <JarvisButton sx={{ ml: 2 }} onClick={handleSendLink}>
            Send Link
          </JarvisButton>
        </Stack>
      </Modal>
    </>
  );
}

export default PHCOnboardingLayout;
